<script lang="ts">
  import WheelCanvas from './WheelCanvas.svelte';
  import { rt, spinRoulette, dismissResult } from '../lib/state.svelte.ts';
  import type { Roulette, Item, RouletteId } from '../lib/types';
  import { buildLayoutForSet } from '../lib/wheelLayout';
  import { appState } from '../lib/state.svelte.ts';

  interface Props {
    /** 表示対象 (visible なルーレットのみ) */
    roulettes: Roulette[];
    /** ルーレット名タップ → アクティブ詳細モード切替 */
    onenterdetail: (id: RouletteId) => void;
  }

  let { roulettes, onenterdetail }: Props = $props();

  function getLayoutFor(r: Roulette) {
    const set = r.sets.find((s) => s.id === r.currentSetId) ?? r.sets[0];
    return buildLayoutForSet(set.items, set.id, appState.ui.shuffleOrder);
  }

  function enabledCountOf(r: Roulette): number {
    const set = r.sets.find((s) => s.id === r.currentSetId) ?? r.sets[0];
    return set.items.filter((it: Item) => it.enabled).length;
  }

  function handleWheelTap(id: RouletteId): void {
    const runtime = rt(id);
    switch (runtime.spinPhase) {
      case 'idle':
        // グリッド表示時は特殊演出を抑制 (シングル表示専用仕様)
        spinRoulette(id, { suppressEffects: true });
        break;
      case 'spinning':
        // スピン中タップは無効
        break;
      case 'done':
        dismissResult(id);
        break;
    }
  }
</script>

<div class="grid-wrap">
<div
  class="grid"
  class:two={roulettes.length === 2}
  class:few={roulettes.length >= 3 && roulettes.length <= 4}
  class:many={roulettes.length >= 5}
>
  {#each roulettes as r (r.id)}
    {@const runtime = rt(r.id)}
    {@const layout = getLayoutFor(r)}
    {@const enabled = enabledCountOf(r)}
    <div class="cell">
      <button
        class="cell-name"
        onclick={() => onenterdetail(r.id)}
        title="詳細を開く"
      >
        <span class="name">{r.name}</span>
        <span class="meta">{enabled} 項目</span>
        <span class="arrow">›</span>
      </button>
      <div class="wheel-area">
        <WheelCanvas
          layout={layout}
          rotation={runtime.wheelAngle}
          settings={r.settings}
          ontap={() => handleWheelTap(r.id)}
        />
        {#if runtime.winnerVisible && runtime.winnerText}
          <div
            class="cell-result"
            role="button"
            tabindex="0"
            onclick={() => dismissResult(r.id)}
            onkeydown={(e) => e.key === 'Enter' && dismissResult(r.id)}
          >
            <span class="result-label">当選</span>
            <span class="result-text">{runtime.winnerText}</span>
          </div>
        {:else if runtime.spinPhase === 'spinning'}
          <div class="cell-status spinning">回転中…</div>
        {:else if enabled === 0}
          <div class="cell-status warn">候補なし</div>
        {/if}
      </div>
    </div>
  {/each}
</div>
  <div class="version-tag">{__APP_VERSION__}</div>
</div>

<style>
  .grid-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: relative;
  }
  .version-tag {
    position: absolute;
    bottom: 2px;
    right: 6px;
    font-size: 9px;
    color: var(--color-fg-muted);
    opacity: 0.55;
    font-family: ui-monospace, 'SFMono-Regular', monospace;
    pointer-events: none;
    white-space: nowrap;
    z-index: 1;
  }

  .grid {
    flex: 1;
    display: grid;
    gap: var(--space-md);
    padding: var(--space-md);
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    align-content: stretch;
    grid-template-columns: 1fr;
  }

  /* 2 件: 縦表示は 1 列ストレッチ (各セル最大化)、横表示は 2 列 */
  .grid.two {
    grid-template-columns: 1fr;
    grid-auto-rows: 1fr;
  }
  @media (orientation: landscape) {
    .grid.two {
      grid-template-columns: 1fr 1fr;
      grid-auto-rows: 1fr;
    }
  }

  /* 3〜4 件: 2 列固定 */
  .grid.few {
    grid-template-columns: 1fr 1fr;
    align-content: start;
  }

  /* 5 件以上: レスポンシブ */
  .grid.many {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    align-content: start;
  }

  .cell {
    display: flex;
    flex-direction: column;
    background: var(--color-surface);
    border-radius: var(--radius-md);
    overflow: hidden;
    border: 1px solid var(--color-border);
    min-height: 0;
  }

  .cell-name {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    border-bottom: 1px solid var(--color-border);
    background: var(--color-bg);
    font-size: var(--font-size-sm);
    text-align: left;
    width: 100%;
    color: var(--color-fg);
  }

  .cell-name:active {
    background: var(--color-border);
  }

  .name {
    flex: 1;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    font-size: 10px;
    color: var(--color-fg-muted);
    background: var(--color-border);
    padding: 1px 6px;
    border-radius: 100px;
    flex-shrink: 0;
  }
  .arrow {
    color: var(--color-fg-muted);
    flex-shrink: 0;
  }

  .wheel-area {
    aspect-ratio: 1 / 1;
    position: relative;
    padding: 6px;
    flex: 1;
    min-height: 0;
    align-self: center;
    width: 100%;
  }
  /* 2 件モード: アスペクト比解除して領域全体使う */
  .grid.two .wheel-area {
    aspect-ratio: auto;
    height: 100%;
  }

  .cell-result {
    position: absolute;
    /* 矩形に変更 (横方向はセル幅いっぱい、上下に余白) */
    left: 4px;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 12px 10px;
    background: rgba(0, 0, 0, 0.85);
    border: 2px solid var(--color-accent);
    border-radius: var(--radius-md);
    cursor: pointer;
    animation: fade-in 0.18s ease-out;
    box-shadow: var(--shadow-md);
  }

  @keyframes fade-in {
    from { opacity: 0; transform: translateY(-50%) scale(0.92); }
    to   { opacity: 1; transform: translateY(-50%) scale(1); }
  }

  .result-label {
    font-size: 10px;
    color: var(--color-accent);
    font-weight: 700;
    letter-spacing: 0.08em;
  }
  .result-text {
    color: #fff;
    font-weight: 700;
    font-size: clamp(15px, 5vw, 22px);
    line-height: 1.25;
    text-align: center;
    word-break: break-word;
    width: 100%;
  }

  .cell-status {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.55);
    color: rgba(255, 255, 255, 0.85);
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 100px;
    pointer-events: none;
    white-space: nowrap;
  }

  .cell-status.warn {
    background: rgba(233, 69, 96, 0.85);
    color: #fff;
  }
</style>
